import type { DictionaryDto } from 'api/generated/schemas';

export interface DictionaryDrivenOption<TValue extends string> {
  value: TValue;
  label: string;
}

/**
 * Merges backend dictionary labels onto a local option list while preserving the
 * local order and default-first ordering (the dictionary's own ordering is
 * alphabetical and does not match the design). The dictionary `description` wins
 * as the label when present; otherwise the local fallback label is kept so the
 * control stays usable before the dictionary query resolves.
 *
 * Pass `localWins` to force specific option values to always use the local label
 * (e.g. when the frontend renames a label before the dictionary is updated).
 */
export const buildDictionaryOptions = <TValue extends string>(
  localOptions: ReadonlyArray<DictionaryDrivenOption<TValue>>,
  dictionary: DictionaryDto[] | undefined,
  localWins?: ReadonlySet<TValue>
): DictionaryDrivenOption<TValue>[] =>
  localOptions.map((option) => {
    if (localWins?.has(option.value)) {
      return { value: option.value, label: option.label };
    }

    const match = dictionary?.find((item) => item.name === option.value);

    return { value: option.value, label: match?.description ?? option.label };
  });
